<template>
  <!-- My Saved Searches drawer list (saved search picker) -->

  <v-card
    width="100%"
    class="favourites"
    :class="{
      'favourites--dark': dark,
      'favourites--layout-debug': layoutDebug,
    }"
    flat
  >
    <header class="favourites__header">
      <v-list class="py-0">
        <v-list-item class="favourites__title px-2 ma-0">
          <v-list-item-icon class="favourites__title-icon-wrap mr-2 my-0">
            <v-icon class="favourites__title-icon" medium>mdi-view-headline</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="py-0">
            <v-list-item-title class="font-weight-bold subtitle-1 favourites__title-text">
              <span>My Saved Searches</span><sup class="favourites__help-sup">
                <a
                  href="https://docs.tendertrace.com/guide/user-documentation/saving-your-work/search-profiles"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Saved searches documentation"
                >
                  <v-icon x-small class="favourites__help-icon">mdi-help-circle-outline</v-icon>
                </a>
              </sup>
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action class="ma-0">
            <v-btn icon aria-label="Refresh saved searches" @click="refreshSavedSearches">
              <v-icon class="favourites__action-icon">mdi-refresh</v-icon>
            </v-btn>
          </v-list-item-action>
          <v-list-item-action class="ma-0">
            <v-btn
              v-if="showLayoutDebugToggle"
              icon
              small
              :aria-pressed="layoutDebug"
              aria-label="Toggle layout guide outlines"
              @click="layoutDebug = !layoutDebug"
            >
              <v-icon x-small>mdi-border-all</v-icon>
            </v-btn>
          </v-list-item-action>
          <v-list-item-action class="ma-0">
            <v-btn icon aria-label="Close saved searches" @click="$emit('closeDrawer')">
              <v-icon class="favourites__close-icon">mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <v-divider class="favourites__divider"></v-divider>
      <v-card flat class="favourites__search-wrap px-2 py-2 mx-2 my-2">
        <v-text-field
          class="favourites__search-field"
          :class="{ 'favourites__search-field--dark': dark }"
          append-icon="mdi-magnify"
          rounded
          v-model="search"
          dense
          :solo="dark"
          :solo-inverted="!dark"
          hide-details
          flat
          label="Search saved searches..."
          color="rgb(255, 20, 99)"
        ></v-text-field>
      </v-card>
    </header>

    <div class="favourites__body">
      <div v-if="loading" class="favourites__loading text-center py-8">
        <v-progress-circular indeterminate color="#ff1463" />
      </div>
      <template v-else>
      <div
        v-if="saved_searches.length > 0"
        class="favourites__notify-toolbar"
      >
        <div
          v-if="!notifyEditMode"
          class="favourites__edit-entry-row favourites__notify-grid"
        >
          <div class="favourites__notify-zone favourites__notify-zone--main">
            <v-tooltip bottom dark color="#000000" style="opacity: 0.85;">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="favourites__edit-entry-btn px-2"
                  :class="{ 'favourites__edit-entry-btn--dark': dark }"
                  aria-label="Bulk edit dashboard and email settings"
                  @click="startNotifyEdit"
                >
                  <span class="favourites__edit-entry-label">
                    Dashboard &amp; Email
                  </span>
                  <v-icon
                    small
                    class="favourites__edit-entry-icon ml-1"
                    aria-hidden="true"
                  >
                    mdi-pencil-box-multiple-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>
                Bulk edit dashboard and email settings for your saved searches.
              </span>
            </v-tooltip>
          </div>
          <div
            class="favourites__notify-zone favourites__notify-zone--dashboard"
            aria-hidden="true"
          />
          <div
            class="favourites__notify-zone favourites__notify-zone--email"
            aria-hidden="true"
          />
        </div>

        <div
          v-else
          class="favourites__edit-toolbar favourites__notify-grid"
          :class="{ 'favourites__edit-toolbar--dark': dark }"
        >
          <div
            class="favourites__notify-zone favourites__notify-zone--main favourites__edit-toolbar-left"
          >
            <span class="favourites__edit-toolbar-summary">
              {{ dirtySummary }}
            </span>
            <div class="favourites__edit-toolbar-actions">
              <v-btn
                text
                x-small
                class="favourites__edit-toolbar-cancel"
                :disabled="savingDraft"
                @click="cancelNotifyEdit"
              >
                Cancel
              </v-btn>
              <v-btn
                color="secondary"
                x-small
                depressed
                class="favourites__edit-toolbar-save"
                :loading="savingDraft"
                :disabled="!notifyDraftDirty || savingDraft"
                @click="saveNotifyDraft"
              >
                Save
              </v-btn>
            </div>
          </div>
          <div
            v-if="filteredSearches.length > 0"
            class="favourites__notify-zone favourites__notify-zone--dashboard"
            @click.stop
          >
            <v-tooltip bottom dark color="#000000" style="opacity: 0.85;">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="favourites__master-toggle-btn"
                  :aria-label="masterDashboardAriaLabel"
                  @click="toggleDraftDashboardForAll"
                >
                  <v-icon small :class="masterDashboardIconClasses">
                    {{ masterDashboardIcon }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ masterDashboardTooltip }}</span>
            </v-tooltip>
          </div>
          <div
            v-if="filteredSearches.length > 0"
            class="favourites__notify-zone favourites__notify-zone--email"
            @click.stop
          >
            <v-tooltip bottom dark color="#000000" style="opacity: 0.85;">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="favourites__master-toggle-btn"
                  :aria-label="masterEmailAriaLabel"
                  @click="toggleDraftEmailForAll"
                >
                  <v-icon small :class="masterEmailIconClasses">
                    {{ masterEmailIcon }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ masterEmailTooltip }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>
      <v-list class="favourites__search-list">
        <v-list-item
          v-for="item in filteredSearches"
          :key="item.id"
          class="favourites__search-row"
          :class="{ 'favourites__search-row--edit': notifyEditMode }"
          :ripple="!notifyEditMode"
          :link="!notifyEditMode"
          @click="onSearchRowClick(item)"
        >
          <v-tooltip left dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on, attrs }">
              <v-list-item-content
                v-bind="attrs"
                v-on="on"
                class="favourites__search-item-content py-2"
              >
                <v-list-item-title
                  class="wrap-text favourites__search-item subtitle-1"
                >
                  <span v-if="item.name && item.name != 'unnamed search'">
                    {{ item.name }}
                  </span>
                  <span v-else>
                    {{ item.plain_text_query }}
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <span class="tooltip">{{ item.plain_text_query }}</span>
          </v-tooltip>
          <v-list-item-action
            class="favourites__notify-zone favourites__notify-zone--dashboard ma-0"
            @click.stop
          >
            <template v-if="!notifyEditMode">
              <v-tooltip bottom dark color="#000000" style="opacity: 0.85;">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    class="favourites__row-toggle-btn"
                    :disabled="togglingId === item.id"
                    :aria-pressed="isDashboardVisible(item)"
                    :aria-label="
                      isDashboardVisible(item)
                        ? 'Shown on dashboard. Click to hide.'
                        : 'Not on dashboard. Click to show.'
                    "
                    v-on="{
                      ...on,
                      click: (e) => {
                        e.stopPropagation();
                        toggleDashboardPin(item);
                      },
                    }"
                  >
                    <v-icon
                      :key="`dashboard-pin-${item.id}-${isDashboardVisible(item)}`"
                      small
                      :class="dashboardPinIconClasses(item)"
                    >
                      {{ dashboardPinIcon(item) }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>
                  {{
                    isDashboardVisible(item)
                      ? "Shown on dashboard. Click to hide."
                      : "Not on dashboard. Click to show."
                  }}
                </span>
              </v-tooltip>
            </template>
            <v-simple-checkbox
              v-else
              class="favourites__edit-checkbox"
              :class="{ 'favourites__edit-checkbox--dark': dark }"
              color="primary"
              :ripple="false"
              :value="draftDashboard(item)"
              :aria-label="`Dashboard for ${displaySearchName(item)}`"
              @input="setDraftDashboard(item, $event)"
            />
          </v-list-item-action>
          <v-list-item-action
            class="favourites__notify-zone favourites__notify-zone--email ma-0"
            @click.stop
          >
            <template v-if="!notifyEditMode">
              <v-tooltip bottom dark color="#000000" style="opacity: 0.85;">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    class="favourites__row-toggle-btn"
                    :disabled="togglingId === item.id"
                    :aria-pressed="isEmailAlertsOn(item)"
                    :aria-label="emailAlertsAriaLabel(item)"
                    v-on="{
                      ...on,
                      click: (e) => {
                        e.stopPropagation();
                        toggleEmailAlerts(item);
                      },
                    }"
                  >
                    <v-icon
                      :key="`email-alerts-${item.id}-${isEmailAlertsOn(item)}`"
                      small
                      :class="emailAlertsIconClasses(item)"
                    >
                      {{ emailAlertsIcon(item) }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ emailAlertsTooltip(item) }}</span>
              </v-tooltip>
            </template>
            <v-simple-checkbox
              v-else
              class="favourites__edit-checkbox"
              :class="{ 'favourites__edit-checkbox--dark': dark }"
              color="primary"
              :ripple="false"
              :value="draftEmail(item)"
              :aria-label="`Email alerts for ${displaySearchName(item)}`"
              @input="setDraftEmail(item, $event)"
            />
          </v-list-item-action>
        </v-list-item>
        <v-list-item
          v-if="filteredSearches.length === 0 && saved_searches.length > 0"
          class="favourites__empty-filter text-center py-4"
        >
        <v-list-item-content>
          <v-list-item-title class="favourites__empty-filter-text">
            No saved searches match your search.
          </v-list-item-title>
        </v-list-item-content>
        </v-list-item>
        <v-list-item
          v-if="saved_searches.length === 0"
          class="ma-0 pa-0 favourites__explainer-outer"
        >
        <v-card flat class="ma-0 pa-0 favourites__explainer-outer-card">
          <v-card-text class="favourites__explainer-wrap pl-3 pt-0">
            <v-list-group
              v-model="explainerOpen"
              class="favourites__explainer"
              no-action
              sub-group
            >
              <template v-slot:activator>
                <v-list-item-content>
                  <v-list-item-title
                    class="subtitle-2 text-decoration-underline font-weight-bold favourites__explainer-heading"
                    color="primary"
                  >
                    Saved Search Explained
                  </v-list-item-title>
                </v-list-item-content>
              </template>
              <v-list-item class="pa-0 favourites__explainer-item">
                <v-card flat tile class="favourites__explainer-content pl-1">
                  <p class="favourites__explainer-text">
                    You can save any searches that you run and quickly rerun them
                    from this list. Icons show dashboard and email status. Tap
                    <strong>Edit dashboard &amp; email</strong> to change
                    settings, then save.
                  </p>
                  <p class="favourites__explainer-text">
                    Saved searches also save the search tab you are on. So for
                    example, if you were on the Leaderboard tab when you saved
                    the search profile, clicking on the saved profile will run
                    the search and also take you to the tab you were on when you
                    saved the profile initially.
                  </p>
                  <p class="favourites__explainer-text">
                    To start saving searches:
                  </p>
                  <ul class="favourites__explainer-text">
                    <li>
                      Run a search for contracts or tenders of interest.
                    </li>
                    <li>
                      In the filter description section of the screen, click
                      the button labeled "Save Search" to have Tendetrace
                      start monitoring that search on your behalf.
                    </li>
                  </ul>
                  <p class="favourites__explainer-link">
                    <router-link
                      class="favourites__explainer-link-text"
                      :to="{
                        name: 'search',
                        query: {
                          disposition: ['Contract Notice', 'Grant'],
                          expiry_from: new Date().getFullYear(),
                          expiry_to: new Date().getFullYear(),
                          sort_order: 'asc'
                        }
                      }"
                      >Click here to start exploring.</router-link
                    >
                  </p>
                </v-card>
              </v-list-item>
            </v-list-group>
          </v-card-text>
        </v-card>
        </v-list-item>
      </v-list>
      </template>
    </div>

    <v-snackbar v-model="snackbar" top :timeout="4000" color="#12253a">
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { bus } from "../main.js";
import axios from "axios";
import { getInstance } from "../auth/index.js";
import {
  updateSavedSearchDashboardPin,
  updateSavedSearchEmailAlerts,
} from "./APIManager.js";
import { primaryNav } from "@/config/appNavigation.js";

const DASHBOARD_NAV_ICON =
  primaryNav.find((entry) => entry.id === "dashboard")?.icon ||
  "mdi-view-dashboard";
const DASHBOARD_PIN_ICON_OFF = "mdi-view-dashboard-outline";

export default {
  props: {
    dark: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: true,
      search: "",
      explainerOpen: false,
      saved_searches: [],
      notifyEditMode: false,
      /** @type {Record<number, { dashboard: boolean, email: boolean }>} */
      draftNotifyById: {},
      savingDraft: false,
      /** Dev: dashed outlines + labels on layout regions. Toggle via grid icon in header. */
      layoutDebug: false,
      showLayoutDebugToggle: process.env.NODE_ENV !== "production",
      snackbar: false,
      snackbarText: "",
      /** Pin state when GET omits is_visible_in_dashboard (cleared once API sends booleans). */
      dashboardPinOverrideById: {},
      /** Saved search id while a per-row dashboard/email PUT is in flight. */
      togglingId: null,
    };
  },

  computed: {
    notifyDraftDirty() {
      return this.saved_searches.some((item) => this.isNotifyRowDirty(item));
    },

    dirtyChangeCount() {
      let count = 0;
      this.saved_searches.forEach((item) => {
        if (!this.isNotifyRowDirty(item)) {
          return;
        }
        if (this.draftDashboard(item) !== this.isDashboardVisible(item)) {
          count += 1;
        }
        if (this.draftEmail(item) !== this.isEmailAlertsOn(item)) {
          count += 1;
        }
      });
      return count;
    },

    dirtySummary() {
      if (!this.notifyDraftDirty) {
        return "No changes";
      }
      const n = this.dirtyChangeCount;
      return `${n} change${n === 1 ? "" : "s"}`;
    },

    editDraftTargets() {
      return this.filteredSearches;
    },

    editAllDashboardOn() {
      const items = this.editDraftTargets;
      return (
        items.length > 0 &&
        items.every((item) => this.draftDashboard(item))
      );
    },

    editDashboardIndeterminate() {
      const items = this.editDraftTargets;
      if (!items.length) {
        return false;
      }
      const onCount = items.filter((item) => this.draftDashboard(item)).length;
      return onCount > 0 && onCount < items.length;
    },

    editAllEmailOn() {
      const items = this.editDraftTargets;
      return (
        items.length > 0 && items.every((item) => this.draftEmail(item))
      );
    },

    editEmailIndeterminate() {
      const items = this.editDraftTargets;
      if (!items.length) {
        return false;
      }
      const onCount = items.filter((item) => this.draftEmail(item)).length;
      return onCount > 0 && onCount < items.length;
    },

    masterDashboardIcon() {
      return this.editAllDashboardOn
        ? DASHBOARD_NAV_ICON
        : DASHBOARD_PIN_ICON_OFF;
    },

    masterDashboardIconClasses() {
      return [
        "favourites__action-toggle-icon",
        "favourites__dashboard-pin-icon",
        this.editAllDashboardOn
          ? "favourites__dashboard-pin-icon--pinned"
          : this.editDashboardIndeterminate
            ? "favourites__dashboard-pin-icon--partial"
            : "favourites__dashboard-pin-icon--unpinned",
      ];
    },

    masterEmailIcon() {
      return this.editAllEmailOn ? "mdi-email-check" : "mdi-email-off-outline";
    },

    masterEmailIconClasses() {
      return [
        "favourites__action-toggle-icon",
        "favourites__email-alerts-icon",
        this.editAllEmailOn
          ? "favourites__email-alerts-icon--on"
          : this.editEmailIndeterminate
            ? "favourites__email-alerts-icon--partial"
            : "favourites__email-alerts-icon--off",
      ];
    },

    masterDashboardTooltip() {
      if (this.editAllDashboardOn) {
        return "Turn off dashboard for all searches in this list";
      }
      return "Turn on dashboard for all searches in this list";
    },

    masterEmailTooltip() {
      if (this.editAllEmailOn) {
        return "Turn off email alerts for all searches in this list";
      }
      return "Turn on email alerts for all searches in this list";
    },

    masterDashboardAriaLabel() {
      return this.masterDashboardTooltip;
    },

    masterEmailAriaLabel() {
      return this.masterEmailTooltip;
    },

    filteredSearches() {
      if (this.saved_searches.length > 0){
        return this.saved_searches.filter(search =>
          (search.name && search.name !== "unnamed search"
            ? search.name
            : search.plain_text_query
          )
            .toLowerCase()
            .includes(this.search.toLowerCase())
        );
      }
      else{
        return this.saved_searches;
      }
      
    }
  },

  mounted() {
    this.getSavedSearches();

    bus.$on("refresh_lists", refresh_lists => {
      this.getSavedSearches();
    });

    bus.$on("search-complete", search_complete => {
      this.getSavedSearches();
    });
  },

  methods: {
    displaySearchName(item) {
      if (item.name && item.name !== "unnamed search") {
        return item.name;
      }
      return item.plain_text_query || "";
    },

    hasDashboardPinFromApi(item) {
      return typeof item.is_visible_in_dashboard === "boolean";
    },

    isDashboardVisible(item) {
      if (this.hasDashboardPinFromApi(item)) {
        return item.is_visible_in_dashboard;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          this.dashboardPinOverrideById,
          item.id
        )
      ) {
        return this.dashboardPinOverrideById[item.id];
      }
      // Until GET includes is_visible_in_dashboard: match launch backfill / legacy digest.
      return item.notify === true;
    },

    /** Filled glyph matches Dashboard in the side nav; outline when unpinned. */
    dashboardPinIcon(item) {
      return this.isDashboardVisible(item)
        ? DASHBOARD_NAV_ICON
        : DASHBOARD_PIN_ICON_OFF;
    },

    dashboardPinIconClasses(item) {
      const pinned = this.isDashboardVisible(item);
      return [
        "favourites__action-toggle-icon",
        "favourites__dashboard-pin-icon",
        pinned
          ? "favourites__dashboard-pin-icon--pinned"
          : "favourites__dashboard-pin-icon--unpinned",
      ];
    },

    isEmailAlertsOn(item) {
      return item.email_alerts === true;
    },

    emailAlertsIcon(item) {
      return this.isEmailAlertsOn(item)
        ? "mdi-email-check"
        : "mdi-email-off-outline";
    },

    emailAlertsIconClasses(item) {
      const on = this.isEmailAlertsOn(item);
      return [
        "favourites__action-toggle-icon",
        "favourites__email-alerts-icon",
        on
          ? "favourites__email-alerts-icon--on"
          : "favourites__email-alerts-icon--off",
      ];
    },

    emailAlertsTooltip(item) {
      if (this.isEmailAlertsOn(item)) {
        return "Email alerts for this search are on. Click to turn off.";
      }
      return "Email alerts for this search are off. Click to turn on.";
    },

    emailAlertsAriaLabel(item) {
      return this.isEmailAlertsOn(item)
        ? "Email alerts on. Click to turn off."
        : "Email alerts off. Click to turn on.";
    },

    applyCurrentSearchFromResponse(item, data) {
      const current = data && data.current_search;
      if (!current || Number(current.id) !== Number(item.id)) {
        return;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          current,
          "is_visible_in_dashboard"
        )
      ) {
        this.$set(
          item,
          "is_visible_in_dashboard",
          current.is_visible_in_dashboard === true
        );
        this.$delete(this.dashboardPinOverrideById, item.id);
      }
      if (Object.prototype.hasOwnProperty.call(current, "email_alerts")) {
        this.$set(item, "email_alerts", current.email_alerts === true);
      }
    },

    showSnackbar(message) {
      this.snackbarText = message;
      this.snackbar = true;
    },

    showError(message) {
      this.showSnackbar(message);
    },

    onSearchRowClick(item) {
      if (this.notifyEditMode) {
        return;
      }
      this.retrieveSearch(item.search_query, item.id);
    },

    buildDraftNotifyFromSaved() {
      const draft = {};
      this.saved_searches.forEach((item) => {
        draft[item.id] = {
          dashboard: this.isDashboardVisible(item),
          email: this.isEmailAlertsOn(item),
        };
      });
      this.draftNotifyById = draft;
    },

    startNotifyEdit() {
      this.buildDraftNotifyFromSaved();
      this.notifyEditMode = true;
    },

    cancelNotifyEdit() {
      this.notifyEditMode = false;
      this.draftNotifyById = {};
    },

    draftDashboard(item) {
      const row = this.draftNotifyById[item.id];
      if (row) {
        return row.dashboard;
      }
      return this.isDashboardVisible(item);
    },

    draftEmail(item) {
      const row = this.draftNotifyById[item.id];
      if (row) {
        return row.email;
      }
      return this.isEmailAlertsOn(item);
    },

    setDraftDashboard(item, value) {
      const next = value === true;
      if (!this.draftNotifyById[item.id]) {
        this.$set(this.draftNotifyById, item.id, {
          dashboard: this.isDashboardVisible(item),
          email: this.isEmailAlertsOn(item),
        });
      }
      this.$set(this.draftNotifyById[item.id], "dashboard", next);
    },

    setDraftEmail(item, value) {
      const next = value === true;
      if (!this.draftNotifyById[item.id]) {
        this.$set(this.draftNotifyById, item.id, {
          dashboard: this.isDashboardVisible(item),
          email: this.isEmailAlertsOn(item),
        });
      }
      this.$set(this.draftNotifyById[item.id], "email", next);
    },

    setDraftDashboardForAll(enabled) {
      this.editDraftTargets.forEach((item) => {
        this.setDraftDashboard(item, enabled);
      });
    },

    setDraftEmailForAll(enabled) {
      this.editDraftTargets.forEach((item) => {
        this.setDraftEmail(item, enabled);
      });
    },

    toggleDraftDashboardForAll() {
      this.setDraftDashboardForAll(!this.editAllDashboardOn);
    },

    toggleDraftEmailForAll() {
      this.setDraftEmailForAll(!this.editAllEmailOn);
    },

    isNotifyRowDirty(item) {
      return (
        this.draftDashboard(item) !== this.isDashboardVisible(item) ||
        this.draftEmail(item) !== this.isEmailAlertsOn(item)
      );
    },

    async applyDashboardPin(item, next) {
      const hadApiField = this.hasDashboardPinFromApi(item);
      const prevItemField = item.is_visible_in_dashboard;
      const hadOverride = Object.prototype.hasOwnProperty.call(
        this.dashboardPinOverrideById,
        item.id
      );
      const prevOverride = this.dashboardPinOverrideById[item.id];

      this.$set(this.dashboardPinOverrideById, item.id, next);
      this.$set(item, "is_visible_in_dashboard", next);

      try {
        const data = await updateSavedSearchDashboardPin({
          searchId: item.id,
          searchName: this.displaySearchName(item),
          isVisibleInDashboard: next,
        });
        this.applyCurrentSearchFromResponse(item, data);
        return true;
      } catch (e) {
        if (hadOverride) {
          this.$set(this.dashboardPinOverrideById, item.id, prevOverride);
        } else {
          this.$delete(this.dashboardPinOverrideById, item.id);
        }
        if (hadApiField) {
          this.$set(item, "is_visible_in_dashboard", prevItemField);
        } else {
          this.$delete(item, "is_visible_in_dashboard");
        }
        return false;
      }
    },

    async applyEmailAlerts(item, next) {
      const prev = item.email_alerts;
      this.$set(item, "email_alerts", next);
      try {
        const data = await updateSavedSearchEmailAlerts({
          searchId: item.id,
          searchName: this.displaySearchName(item),
          emailAlerts: next,
        });
        this.applyCurrentSearchFromResponse(item, data);
        return true;
      } catch (e) {
        this.$set(item, "email_alerts", prev);
        return false;
      }
    },

    async toggleDashboardPin(item) {
      if (this.togglingId != null) {
        return;
      }
      const next = !this.isDashboardVisible(item);
      this.togglingId = item.id;
      try {
        const ok = await this.applyDashboardPin(item, next);
        if (ok) {
          bus.$emit("refresh-daily-digest");
        } else {
          this.showError(
            "Could not update dashboard setting. Please try again."
          );
        }
      } finally {
        this.togglingId = null;
      }
    },

    async toggleEmailAlerts(item) {
      if (this.togglingId != null) {
        return;
      }
      const next = !this.isEmailAlertsOn(item);
      this.togglingId = item.id;
      try {
        const ok = await this.applyEmailAlerts(item, next);
        if (!ok) {
          this.showError("Could not update email alerts. Please try again.");
        }
      } finally {
        this.togglingId = null;
      }
    },

    async saveNotifyDraft() {
      if (!this.notifyDraftDirty || this.savingDraft) {
        return;
      }

      const dirtyItems = this.saved_searches.filter((item) =>
        this.isNotifyRowDirty(item)
      );
      this.savingDraft = true;
      let failed = 0;
      let dashboardChanged = false;

      try {
        for (const item of dirtyItems) {
          const wantDashboard = this.draftDashboard(item);
          const wantEmail = this.draftEmail(item);

          if (wantDashboard !== this.isDashboardVisible(item)) {
            const ok = await this.applyDashboardPin(item, wantDashboard);
            if (!ok) {
              failed += 1;
            } else {
              dashboardChanged = true;
            }
          }

          if (wantEmail !== this.isEmailAlertsOn(item)) {
            const ok = await this.applyEmailAlerts(item, wantEmail);
            if (!ok) {
              failed += 1;
            }
          }
        }

        if (dashboardChanged) {
          bus.$emit("refresh-daily-digest");
        }

        if (failed === 0) {
          this.showSnackbar("Saved search notifications updated.");
          this.cancelNotifyEdit();
        } else {
          this.showError(
            `Some changes could not be saved (${failed} failed). Review and try again.`
          );
          this.buildDraftNotifyFromSaved();
        }
      } finally {
        this.savingDraft = false;
      }
    },

    retrieveSearch(query, search_id) {
      const qry = { ...query, search_id };
      this.$router.push({ path: "/search", query: qry });
      bus.$emit("hide-side-navs", true);
    },

    refreshSavedSearches() {
      this.loading = true;
      this.getSavedSearches();
    },

    async getSavedSearches() {
      const instance = getInstance();
      const accessToken = await instance.getTokenSilently();

      axios
        .get(process.env.VUE_APP_API_SEARCHES, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        })
        .then(response => {
          this.saved_searches = response.data.saved_searches;
          this.saved_searches.forEach(s => {
            if (typeof s.is_visible_in_dashboard === "boolean") {
              this.$delete(this.dashboardPinOverrideById, s.id);
            }
          });
          if (this.notifyEditMode) {
            this.buildDraftNotifyFromSaved();
          }
          this.explainerOpen = this.saved_searches.length === 0;
          this.loading = false;
          return this.saved_searches;
        })
        .catch(() => {
          this.showError("Could not load saved searches. Please try again.");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.favourites {
  --fav-notify-col-width: 40px;
  --fav-notify-zone-gap: 16px;
  --fav-notify-cols-inset-right: 12px;
  --fav-notify-row-height: 40px;
  --fav-notify-row-pad-x: 16px;
  --fav-notify-grid-columns: minmax(0, 1fr) var(--fav-notify-col-width)
    var(--fav-notify-col-width);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.favourites__notify-grid {
  display: grid;
  grid-template-columns: var(--fav-notify-grid-columns);
  column-gap: var(--fav-notify-zone-gap);
  align-items: center;
  box-sizing: border-box;
  min-height: var(--fav-notify-row-height);
  padding-left: var(--fav-notify-row-pad-x);
  padding-right: calc(
    var(--fav-notify-row-pad-x) + var(--fav-notify-cols-inset-right)
  );
}

.favourites__notify-zone--main {
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px 6px;
}

.favourites__edit-entry-row.favourites__notify-grid
  .favourites__notify-zone--main {
  justify-content: flex-end;
}

.favourites__notify-zone--dashboard,
.favourites__notify-zone--email {
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--fav-notify-col-width);
  justify-self: center;
}

.favourites__header {
  flex-shrink: 0;
}

.favourites__title.v-list-item {
  min-height: 48px;
}

.favourites__title-icon-wrap {
  align-self: center;
  margin-right: 8px !important;
  min-width: auto;
}

.favourites__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.favourites__notify-toolbar {
  flex-shrink: 0;
  padding: 0;
}

.favourites >>> .favourites__search-list.v-list {
  padding: 0;
  background: transparent;
}

.favourites__title-icon,
.favourites__title-text {
  color: #12253a !important;
}

.favourites__help-sup {
  line-height: 0;
  margin-left: 2px;
}

.favourites__help-sup a {
  text-decoration: none;
  opacity: 0.85;
}

.favourites__help-sup a:hover {
  opacity: 1;
}

.favourites__help-icon {
  color: #12253a !important;
}

.favourites__action-icon {
  color: #12253a !important;
}

.favourites__close-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

.favourites__search-item {
  color: #12253a !important;
}

.favourites__explainer-heading,
.favourites__explainer-text {
  color: #12253a;
}

.favourites__explainer-link a {
  color: #12253a;
}

.favourites__empty-filter-text {
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.875rem;
}

.favourites--dark {
  background: transparent !important;
}

.favourites--dark .favourites__title-icon,
.favourites--dark .favourites__title-text {
  color: rgba(255, 255, 255, 0.95) !important;
}

.favourites--dark .favourites__action-icon,
.favourites--dark .favourites__close-icon,
.favourites--dark .favourites__help-icon {
  color: rgba(255, 255, 255, 0.9) !important;
}

.favourites--dark .favourites__divider {
  border-color: rgba(255, 255, 255, 0.12);
}

.favourites--dark .favourites__search-item {
  color: rgba(255, 255, 255, 0.9) !important;
}

.favourites--dark .favourites__explainer-heading,
.favourites--dark .favourites__explainer-text {
  color: rgba(255, 255, 255, 0.85);
}

.favourites--dark .favourites__explainer-link a,
.favourites--dark .favourites__explainer-link-text {
  color: #ff1463 !important;
}

.favourites--dark .favourites__explainer-link a:hover,
.favourites--dark .favourites__explainer-link-text:hover {
  color: #ff4d8a !important;
}

.favourites--dark .favourites__empty-filter-text {
  color: rgba(255, 255, 255, 0.6) !important;
}

.favourites--dark .favourites__search-wrap {
  background: transparent !important;
}

.favourites__search-field--dark >>> .v-input__slot {
  background: #ffffff !important;
  box-shadow: none !important;
}

.favourites__search-field--dark >>> fieldset {
  border: 1px solid #12253a !important;
}

.favourites__search-field--dark >>> .v-input__append-inner .v-icon {
  color: #ff1463 !important;
}

.favourites__search-field--dark >>> input {
  color: #12253a !important;
  caret-color: #12253a;
}

.favourites__search-field--dark >>> .v-label {
  color: rgba(18, 37, 58, 0.6) !important;
}

.favourites__search-field--dark.v-input--is-focused >>> fieldset {
  border-color: #12253a !important;
}

.favourites--dark >>> .v-list-item__title,
.favourites--dark >>> .v-list-item__icon .v-icon {
  color: rgba(255, 255, 255, 0.85) !important;
}

.favourites--dark .favourites__explainer-wrap {
  background: #12253a !important;
}

.favourites--dark .favourites__explainer-content,
.favourites--dark .favourites__explainer-item,
.favourites--dark .favourites__explainer-outer,
.favourites--dark .favourites__explainer-outer-card {
  background: #12253a !important;
}

.favourites--dark >>> .favourites__explainer .v-list-group__header,
.favourites--dark >>> .favourites__explainer .v-list-group__items,
.favourites--dark >>> .favourites__explainer .v-list-group__items .v-list-item,
.favourites--dark >>> .favourites__explainer .v-list-group__items .v-card {
  background: #12253a !important;
}

.favourites__edit-entry-btn {
  text-transform: none;
  letter-spacing: normal;
  font-size: 0.8125rem;
  color: #12253a !important;
  height: 28px !important;
  min-height: 28px !important;
}

.favourites__edit-entry-btn >>> .v-btn__content {
  display: inline-flex;
  align-items: center;
  color: inherit !important;
}

.favourites__edit-entry-icon {
  color: inherit !important;
  font-size: 16px !important;
}

.favourites__edit-entry-btn--dark {
  color: rgba(255, 255, 255, 0.75) !important;
}

.favourites__edit-entry-btn--dark:hover,
.favourites__edit-entry-btn--dark:focus {
  color: rgba(255, 255, 255, 0.95) !important;
}

.favourites__edit-entry-btn--dark >>> .v-btn__content,
.favourites__edit-entry-btn--dark .favourites__edit-entry-icon {
  color: inherit !important;
}

.favourites__edit-toolbar-left {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.favourites__edit-toolbar-summary {
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.54);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;
}

.favourites__edit-toolbar--dark .favourites__edit-toolbar-summary {
  color: rgba(255, 255, 255, 0.55);
}

.favourites__edit-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.favourites__edit-toolbar-actions >>> .v-btn {
  min-height: 26px !important;
  height: 26px !important;
  padding: 0 8px !important;
}

.favourites__edit-toolbar-cancel {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.6875rem !important;
}

.favourites__edit-toolbar--dark .favourites__edit-toolbar-cancel {
  color: rgba(255, 255, 255, 0.9) !important;
}

.favourites__edit-toolbar-save {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.6875rem !important;
}

.favourites__master-toggle-btn.v-btn {
  width: var(--fav-notify-col-width) !important;
  height: 28px !important;
  min-width: var(--fav-notify-col-width) !important;
  min-height: 28px !important;
}

.favourites__dashboard-pin-icon--partial,
.favourites__email-alerts-icon--partial {
  color: rgba(255, 20, 99, 0.55) !important;
}

.favourites--dark .favourites__dashboard-pin-icon--partial,
.favourites--dark .favourites__email-alerts-icon--partial {
  color: rgba(255, 20, 99, 0.65) !important;
}

.favourites__edit-checkbox >>> .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
  height: 28px;
  min-height: 28px !important;
}

.favourites__edit-checkbox >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.favourites__edit-checkbox >>> .v-input--selection-controls__input::before,
.favourites__edit-checkbox >>> .v-input--selection-controls__input::after {
  content: none !important;
  opacity: 0 !important;
  background: transparent !important;
  transform: none !important;
}

.favourites--dark
  .favourites__edit-checkbox
  >>> .v-input--selection-controls:not(.v-input--is-disabled)
  .v-input--selection-controls__input:hover::before,
.favourites--dark
  .favourites__edit-checkbox
  >>> .v-input--selection-controls__input::before {
  content: none !important;
  opacity: 0 !important;
}

.favourites__edit-checkbox
  >>> .v-input--selection-controls__input:hover::before,
.favourites__edit-checkbox
  >>> .v-input--selection-controls__input:focus::before,
.favourites__edit-checkbox
  >>> .v-input--is-focused
  .v-input--selection-controls__input::before {
  opacity: 0 !important;
}

.favourites__edit-checkbox >>> .v-input--selection-controls__ripple,
.favourites__edit-checkbox >>> .v-ripple__container,
.favourites__edit-checkbox >>> .v-ripple__animation {
  display: none !important;
  opacity: 0 !important;
}

.favourites >>> .favourites__search-row--edit .v-list-item__action .v-ripple__container {
  display: none !important;
}

.favourites__edit-checkbox >>> .v-icon {
  font-size: 20px !important;
}

.favourites__edit-checkbox--dark >>> .v-icon:not(.primary--text) {
  color: rgba(255, 255, 255, 0.85) !important;
}

.favourites__edit-checkbox--dark >>> .v-icon.primary--text {
  color: #ff1463 !important;
}

.favourites__edit-checkbox--dark >>> .mdi-minus-box:not(.primary--text) {
  color: rgba(255, 255, 255, 0.85) !important;
}

.favourites__edit-checkbox--dark >>> .mdi-minus-box.primary--text {
  color: #ff1463 !important;
}

.favourites >>> .favourites__search-row.v-list-item {
  display: grid !important;
  grid-template-columns: var(--fav-notify-grid-columns);
  column-gap: var(--fav-notify-zone-gap);
  align-items: center;
  box-sizing: border-box;
  min-height: 0 !important;
  height: auto !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-left: var(--fav-notify-row-pad-x) !important;
  padding-right: calc(
    var(--fav-notify-row-pad-x) + var(--fav-notify-cols-inset-right)
  ) !important;
}

.favourites >>> .favourites__search-row.v-list-item::before {
  top: 0 !important;
  bottom: 0 !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.favourites >>> .favourites__search-row > .v-tooltip {
  display: contents;
}

.favourites >>> .favourites__search-row .v-list-item__content {
  grid-column: 1;
  grid-row: 1;
  min-width: 0;
  align-self: center;
}

.favourites >>> .favourites__search-row .v-list-item__title.favourites__search-item {
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 400;
}

.favourites >>> .favourites__search-row .v-list-item__action {
  margin: 0 !important;
  padding: 0 !important;
  min-width: var(--fav-notify-col-width) !important;
  align-self: center;
  grid-row: 1;
}

.favourites >>> .favourites__search-row .favourites__notify-zone--dashboard {
  grid-column: 2;
}

.favourites >>> .favourites__search-row .favourites__notify-zone--email {
  grid-column: 3;
}

.favourites__search-row {
  cursor: pointer;
}

.favourites__search-row--edit {
  cursor: default;
}

.favourites >>> .favourites__search-row--edit.v-list-item::before,
.favourites >>> .favourites__search-row--edit.v-list-item:hover::before,
.favourites >>> .favourites__search-row--edit.v-list-item--highlighted::before,
.favourites >>> .favourites__search-row--edit.v-list-item:focus::before,
.favourites >>> .favourites__search-row--edit.v-list-item--active::before {
  opacity: 0 !important;
}

.favourites__search-row--edit:hover .favourites__search-item {
  text-decoration: none !important;
}

.favourites__row-toggle-btn.v-btn {
  width: var(--fav-notify-col-width) !important;
  height: 28px !important;
  min-width: var(--fav-notify-col-width) !important;
  min-height: 28px !important;
}

.favourites__action-toggle-icon {
  color: rgba(0, 0, 0, 0.38) !important;
}

.favourites__action-toggle-icon--active {
  color: rgb(255, 20, 99) !important;
}

.favourites__dashboard-pin-icon--pinned,
.favourites__dashboard-pin-icon--unpinned {
  color: rgba(0, 0, 0, 0.38) !important;
}

.favourites--dark .favourites__action-toggle-icon {
  color: rgba(255, 255, 255, 0.45) !important;
}

.favourites--dark .favourites__action-toggle-icon--active {
  color: rgb(255, 20, 99) !important;
}

.favourites--dark .favourites__dashboard-pin-icon--pinned,
.favourites--dark .favourites__dashboard-pin-icon--unpinned {
  color: rgba(255, 255, 255, 0.45) !important;
}

.favourites__email-alerts-icon--on,
.favourites__email-alerts-icon--off {
  color: rgba(0, 0, 0, 0.38) !important;
}

.favourites--dark .favourites__email-alerts-icon--on,
.favourites--dark .favourites__email-alerts-icon--off {
  color: rgba(255, 255, 255, 0.45) !important;
}

.favourites__search-item-content {
  width: 100%;
}

.favourites__search-row:hover .favourites__search-item {
  text-decoration: underline !important;
}

.favourites--dark >>> .favourites__search-row .v-list-item__content {
  color: rgba(255, 255, 255, 0.9) !important;
}

.v-application--is-ltr .v-list-group--sub-group .v-list-group__header {
  padding-left: 0px !important;
}

.wrap-text {
  -webkit-line-clamp: unset !important;
}

.tooltip {
  display: inline-block;
  max-width: 360px;
}

/* Layout debug: dev only (header grid icon when NODE_ENV !== production) */
.favourites--layout-debug .favourites__header,
.favourites--layout-debug .favourites__body,
.favourites--layout-debug .favourites__search-wrap,
.favourites--layout-debug .favourites__notify-toolbar,
.favourites--layout-debug .favourites__edit-entry-row,
.favourites--layout-debug .favourites__edit-toolbar,
.favourites--layout-debug .favourites__edit-toolbar-left,
.favourites--layout-debug .favourites__edit-toolbar-actions,
.favourites--layout-debug .favourites__notify-grid,
.favourites--layout-debug .favourites__notify-zone--main,
.favourites--layout-debug .favourites__notify-zone--dashboard,
.favourites--layout-debug .favourites__notify-zone--email,
.favourites--layout-debug .favourites__search-row,
.favourites--layout-debug .favourites__search-item-content,
.favourites--layout-debug .favourites__master-toggle-btn,
.favourites--layout-debug .favourites__row-toggle-btn,
.favourites--layout-debug .favourites__edit-checkbox {
  position: relative;
  outline-style: dashed;
  outline-width: 1px;
  outline-offset: -1px;
}

.favourites--layout-debug .favourites__header {
  outline-color: #ff1463;
}

.favourites--layout-debug .favourites__body {
  outline-color: #00bcd4;
}

.favourites--layout-debug .favourites__search-wrap {
  outline-color: #ffc107;
}

.favourites--layout-debug .favourites__notify-toolbar {
  outline-color: #9c27b0;
}

.favourites--layout-debug .favourites__edit-entry-row,
.favourites--layout-debug .favourites__edit-toolbar {
  outline-color: #e91e63;
}

.favourites--layout-debug .favourites__edit-toolbar-left {
  outline-color: #3f51b5;
}

.favourites--layout-debug .favourites__edit-toolbar-actions {
  outline-color: #009688;
}

.favourites--layout-debug .favourites__notify-grid {
  outline-color: #673ab7;
}

.favourites--layout-debug .favourites__notify-zone--main {
  outline-color: #3f51b5;
}

.favourites--layout-debug .favourites__notify-zone--dashboard {
  outline-color: #ff5722;
}

.favourites--layout-debug .favourites__notify-zone--email {
  outline-color: #03a9f4;
}

.favourites--layout-debug .favourites__search-row {
  outline-color: rgba(255, 255, 255, 0.45);
}

.favourites--layout-debug .favourites__search-item-content {
  outline-color: #8bc34a;
}

.favourites--layout-debug .favourites__master-toggle-btn,
.favourites--layout-debug .favourites__row-toggle-btn {
  outline-color: rgba(255, 87, 34, 0.9);
}

.favourites--layout-debug .favourites__edit-checkbox {
  outline-color: rgba(156, 39, 176, 0.9);
}

.favourites--layout-debug .favourites__header::before,
.favourites--layout-debug .favourites__body::before,
.favourites--layout-debug .favourites__search-wrap::before,
.favourites--layout-debug .favourites__notify-toolbar::before,
.favourites--layout-debug .favourites__edit-entry-row::before,
.favourites--layout-debug .favourites__edit-toolbar::before,
.favourites--layout-debug .favourites__edit-toolbar-left::before,
.favourites--layout-debug .favourites__edit-toolbar-actions::before,
.favourites--layout-debug .favourites__notify-grid::before,
.favourites--layout-debug .favourites__notify-zone--main::before,
.favourites--layout-debug .favourites__notify-zone--dashboard::before,
.favourites--layout-debug .favourites__notify-zone--email::before,
.favourites--layout-debug .favourites__search-row::before,
.favourites--layout-debug .favourites__search-item-content::before {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  padding: 0 3px;
  font-size: 9px;
  line-height: 12px;
  font-family: monospace;
  font-weight: 600;
  color: #12253a;
  background: rgba(255, 255, 255, 0.92);
  pointer-events: none;
}

.favourites--layout-debug.favourites--dark .favourites__header::before,
.favourites--layout-debug.favourites--dark .favourites__body::before,
.favourites--layout-debug.favourites--dark .favourites__search-wrap::before,
.favourites--layout-debug.favourites--dark .favourites__notify-toolbar::before,
.favourites--layout-debug.favourites--dark .favourites__edit-entry-row::before,
.favourites--layout-debug.favourites--dark .favourites__edit-toolbar::before,
.favourites--layout-debug.favourites--dark .favourites__edit-toolbar-left::before,
.favourites--layout-debug.favourites--dark .favourites__edit-toolbar-actions::before,
.favourites--layout-debug.favourites--dark .favourites__notify-grid::before,
.favourites--layout-debug.favourites--dark .favourites__notify-zone--main::before,
.favourites--layout-debug.favourites--dark .favourites__notify-zone--dashboard::before,
.favourites--layout-debug.favourites--dark .favourites__notify-zone--email::before,
.favourites--layout-debug.favourites--dark .favourites__search-row::before,
.favourites--layout-debug.favourites--dark .favourites__search-item-content::before {
  color: #fff;
  background: rgba(18, 37, 58, 0.88);
}

.favourites--layout-debug .favourites__header::before {
  content: "header";
}

.favourites--layout-debug .favourites__body::before {
  content: "body (scroll)";
}

.favourites--layout-debug .favourites__search-wrap::before {
  content: "search-wrap";
}

.favourites--layout-debug .favourites__notify-toolbar::before {
  content: "notify-toolbar";
}

.favourites--layout-debug .favourites__edit-entry-row::before {
  content: "edit-entry-row";
}

.favourites--layout-debug .favourites__edit-toolbar::before {
  content: "edit-toolbar";
}

.favourites--layout-debug .favourites__edit-toolbar-left::before {
  content: "toolbar-left";
}

.favourites--layout-debug .favourites__edit-toolbar-actions::before {
  content: "cancel/save";
}

.favourites--layout-debug .favourites__notify-grid::before {
  content: "3-col grid";
}

.favourites--layout-debug .favourites__notify-zone--main::before {
  content: "zone: main";
}

.favourites--layout-debug .favourites__notify-zone--dashboard::before {
  content: "zone: dashboard";
}

.favourites--layout-debug .favourites__notify-zone--email::before {
  content: "zone: email";
}

.favourites--layout-debug .favourites__search-row::before {
  content: "search-row";
}

.favourites--layout-debug .favourites__search-item-content::before {
  content: "title";
}
</style>
